export const allDimensions = ['assignments', 'questions', 'standards', 'students'] as const;

export const TOP_LEVEL_FILTERS = ['classrooms', 'teachers', 'schools', 'districts'] as const;
export type TopLevelFilter = (typeof TOP_LEVEL_FILTERS)[number];

export type Dimension = (typeof allDimensions)[number];

export type FilterTypes =
  | 'schools'
  | 'districts'
  | 'teachers'
  | 'assignments'
  | 'classrooms'
  | 'guesses'
  | 'guide_levels'
  | 'questions'
  | 'standards'
  | 'standard_sets'
  | 'students'
  | 'subjects';

export type OperatorTypes =
  | 'equals'
  | 'not_equals'
  | 'set'
  | 'not_set'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'contains'
  | 'most_recent'
  | 'search';

export type Sort = {
  type: string;
  field: string;
  direction: 'asc' | 'desc';
};

export type Pagination = {
  type: Dimension;
  value: {
    limit: number;
    offset: number;
  };
}[];

export type Filter = {
  type: FilterTypes;
  field: string;
  operator: OperatorTypes;
  values: string[] | number[];
};

export type QueryVariables = {
  assignmentFilter?: Filter;
  attemptNumberFilter?: Filter | Omit<Filter, 'field' | 'values'>;
  classroomFilter?: Filter;
  teacherFilter?: Filter;
  schoolFilter?: Filter;
  districtFilter?: Filter;
  endDateFilter?: Filter;
  guideLevelFilter?: Filter;
  questionFilter?: Filter;
  standardFilter?: Filter;
  standardSetFilter?: Filter;
  startDateFilter?: Filter;
  studentFilter?: Filter;
  subjectFilter?: Filter;
};

export type MetricField = `${'guess_metrics' | 'question_metrics'}.${string}`;

interface ColumnBase {
  initialSortDirection?: SortOption;
  primaryColumn?: boolean;
  key: string;
}

interface MetricColumnBase extends ColumnBase {
  columnType: 'metric';
  sortKey: `${'guess_metrics' | 'question_metrics'}.${string}` | null;
  isAvailable?: (
    dimensions: Dimension[],
    filters: QueryVariables,
    topLevelFilterType: TopLevelFilter
  ) => boolean;
  getMetricsToLoad: (
    dimensions: Dimension[],
    filters: QueryVariables,
    opts: {performanceCalculateBy: 'accuracy' | 'grade'}
  ) => MetricField[];
  renderColumn: (
    data: AnalyticsApiResponse,
    dimensions: Dimension[],
    filters: QueryVariables
  ) => JSX.Element;
  // metric columns can map to multiple metrics, so we have to know what the headers are and
  // be able to return multiple headers/values. It is the responsibility of each MetricColumn definition
  // to make sure the number of headers and data are the same.
  exportHeaders: (
    dimensions: Dimension[],
    filters: QueryVariables,
    opts: {performanceCalculateBy: 'accuracy' | 'grade'}
  ) => string[];
  export: (
    metrics: AnalyticsApiResponse,
    dimensions: Dimension[],
    filters: QueryVariables,
    opts: {performanceCalculateBy: 'accuracy' | 'grade'}
  ) => string[];
}

interface DefaultMetricColumn extends MetricColumnBase {
  metricType: 'default';
  title: string;
}

interface MetricColumnWithRenderedTitle extends MetricColumnBase {
  metricType: 'renderedTitle';
  renderTitle: ({
    dimensions,
    variables
  }: {
    dimensions: Dimension[];
    variables: QueryVariables;
  }) => JSX.Element;
}

export type MetricColumn = DefaultMetricColumn | MetricColumnWithRenderedTitle;

type SortKey = `${string}.${string}` | null;

export type DimensionColumn = ColumnBase &
  ({
    columnType: 'dimension';
    dimension: Dimension;
    title: string;
    sortKey: SortKey | ((dimensions: Dimension[]) => SortKey);
    // if no isAvailable is provided, the column will always be available
    isAvailable?: (
      dimensions: Dimension[],
      filters: QueryVariables,
      topLevelFilterType: TopLevelFilter
    ) => boolean;
    afterMetrics?: boolean;
    renderColumn: (
      dimensionData: AnalyticsApiResponse['dimensions'],
      dimensions: Dimension[]
    ) => JSX.Element;
  } & (
    | {
        // raw export for CSV - null means this is not an exportable column
        exportHeaders: (dimensions: Dimension[]) => string[];
        export: (dimensions: AnalyticsApiResponse['dimensions']) => string[];
      }
    | {exportHeaders: null; export: null}
  ));

export type Column = DimensionColumn | MetricColumn;

export type SortProps =
  | {defaultSort?: false; sortBy?: string}
  | {defaultSort: true; sortBy: string};

export type ColumnProps = {
  className?: string;
  content: (props) => React.ReactNode;
  header?: React.ReactNode;
  isActionMenu?: boolean;
} & SortProps;

export type SortOption = 'asc' | 'desc';

export type GuessMetricResponse = {
  type: 'guess_metrics';
  values: {
    accuracy: number;
    avg_time_spent: number;
    avg_time_spent_per_student: number;
    count_attempts: number;
    count_correct_attempts: number;
    count_incorrect_attempts: number;
    max_attempts?: number;
    total_time_spent: number;
  };
};

export type QuestionMetricResponse = {
  type: 'question_metrics';
  values: {
    count_questions: number;
    count_unattempted: number;
    grade: number;
  };
};

export interface AnalyticsApiResponse {
  dimensions: {
    type: 'students' | 'assignments' | 'questions' | 'standards';
    value: any;
  }[];
  metrics: (GuessMetricResponse | QuestionMetricResponse)[];
}
